// Liabilities.jsx — Mortgage Matrix (Refinance Flow)
// v13.0 — Single liabilities list (no Applicant 1 / Applicant 2 split)

import React, { useState, useEffect } from "react";
import CurrencyInput from "../ClientForm/CurrencyInput";
import styles from "./RefinanceForm.module.css";

function Liabilities({ formData, setFormData, prevStep, nextStep }) {
  // ----------------------------------------------------------
  // 1️⃣ Helper to build empty row
  // ----------------------------------------------------------
  const emptyLiability = () => ({
    type: "",
    creditor: "",
    limit: "",
    balance: "",
    repayment: "",
    interestRate: "",
  });

  // ----------------------------------------------------------
  // 2️⃣ Build initial liabilities (from Lambda prefills)
  // ----------------------------------------------------------
  const buildInitialLiabilities = () => {
    const all = [];

    // ---- HOME LOANS ----
    (formData.mortgageLoans || []).forEach((l) =>
      all.push({
        type: "Home Loan",
        creditor: l.creditor || "",
        limit: l.limit || "",
        balance: l.balance || "",
        interestRate: l.interestRate || "",
        repayment: l.repayment || "",
      })
    );

    // ---- CREDIT CARDS ----
    (formData.creditCards || []).forEach((l) =>
      all.push({
        type: "Credit Card",
        creditor: l.creditor || "",
        limit: l.limit || "",
        balance: l.balance || "",
      })
    );

    // ---- PERSONAL LOANS ----
    (formData.personalLoans || []).forEach((l) =>
      all.push({
        type: "Personal Loan",
        creditor: l.creditor || "",
        balance: l.balance || "",
        repayment: l.repayment || "",
      })
    );

    // ---- CAR LOANS ----
    (formData.carLoans || []).forEach((l) =>
      all.push({
        type: "Car Loan",
        creditor: l.creditor || "",
        balance: l.balance || "",
        repayment: l.repayment || "",
      })
    );

    // ---- HECS ----
    (formData.hecs || []).forEach((l) =>
      all.push({
        type: "HECS/HELP Debt",
        balance: l.balance || "",
      })
    );

    return all.length > 0 ? all : [emptyLiability()];
  };

  // ----------------------------------------------------------
  // 3️⃣ Local state — single list
  // ----------------------------------------------------------
  const [liabilities, setLiabilities] = useState(buildInitialLiabilities);

  // ----------------------------------------------------------
  // 4️⃣ Sync into formData whenever changed
  // ----------------------------------------------------------
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      liabilities,
    }));
  }, [liabilities, setFormData]);

  // ----------------------------------------------------------
  // 5️⃣ CRUD helpers
  // ----------------------------------------------------------
  const updateLiability = (index, field, value) => {
    setLiabilities((prev) => {
      const list = [...prev];
      list[index] = { ...list[index], [field]: value };
      return list;
    });
  };

  const addLiability = () => {
    setLiabilities((prev) => [...prev, emptyLiability()]);
  };

  const removeLiability = (index) => {
    setLiabilities((prev) => prev.filter((_, i) => i !== index));
  };

  // ----------------------------------------------------------
  // 6️⃣ Render liabilities list
  // ----------------------------------------------------------
  return (
    <div className={styles.container}>
      <h3>Liabilities</h3>
      <p>Add any existing debts or obligations.</p>

      {liabilities.map((l, index) => (
        <div key={index} className={styles.formRow}>
          {/* Type */}
          <select
            value={l.type}
            onChange={(e) => updateLiability(index, "type", e.target.value)}
          >
            <option value="">Select Liability</option>
            <option value="Home Loan">Home Loan</option>
            <option value="Credit Card">Credit Card</option>
            <option value="Personal Loan">Personal Loan</option>
            <option value="Car Loan">Car Loan</option>
            <option value="HECS/HELP Debt">HECS/HELP Debt</option>
            <option value="Other">Other</option>
          </select>

          {/* Creditor */}
          {l.type && (
            <input
              type="text"
              placeholder="Lender / Institution"
              value={l.creditor || ""}
              onChange={(e) =>
                updateLiability(index, "creditor", e.target.value)
              }
            />
          )}

          {/* HOME LOAN */}
          {l.type === "Home Loan" && (
            <>
              <CurrencyInput
                value={l.limit}
                onChange={(val) => updateLiability(index, "limit", val)}
                placeholder="Loan Limit ($)"
              />

              <CurrencyInput
                value={l.balance}
                onChange={(val) => updateLiability(index, "balance", val)}
                placeholder="Current Balance ($)"
              />

              <div className={styles.currencyInputWithSuffix}>
                <input
                  type="text"
                  placeholder="Interest Rate"
                  value={l.interestRate || ""}
                  onChange={(e) =>
                    updateLiability(index, "interestRate", e.target.value)
                  }
                />
                <span className={styles.suffix}>%</span>
              </div>

              <CurrencyInput
                value={l.repayment}
                onChange={(val) => updateLiability(index, "repayment", val)}
                placeholder="Monthly Repayment ($)"
              />
            </>
          )}

          {/* CREDIT CARD */}
          {l.type === "Credit Card" && (
            <CurrencyInput
              value={l.limit}
              onChange={(val) => updateLiability(index, "limit", val)}
              placeholder="Card Limit ($)"
            />
          )}

          {/* PERSONAL / CAR LOAN */}
          {(l.type === "Personal Loan" || l.type === "Car Loan") && (
            <>
              <CurrencyInput
                value={l.balance}
                onChange={(val) => updateLiability(index, "balance", val)}
                placeholder="Balance ($)"
              />
              <CurrencyInput
                value={l.repayment}
                onChange={(val) => updateLiability(index, "repayment", val)}
                placeholder="Monthly Repayment ($)"
              />
            </>
          )}

          {/* HECS */}
          {l.type === "HECS/HELP Debt" && (
            <CurrencyInput
              value={l.balance}
              onChange={(val) => updateLiability(index, "balance", val)}
              placeholder="Balance ($)"
            />
          )}

          {/* REMOVE BUTTON */}
          <button
            type="button"
            className={styles.removeBtn}
            onClick={() => removeLiability(index)}
          >
            ✕
          </button>
        </div>
      ))}

      <button type="button" className={styles.addBtn} onClick={addLiability}>
        + Add Liability
      </button>

      <div className={styles.navBtns}>
        <button type="button" onClick={prevStep}>
          ← Back
        </button>
        <button type="button" className={styles.nextBtn} onClick={nextStep}>
          Submit Application
        </button>
      </div>
    </div>
  );
}

export default Liabilities;
